// Wise history: transfers made before HHL Ops (or in the Wise app), read from Wise (with a token) or a statement CSV
// (without one), each with what it probably paid: a bill, a PO's deposit or balance, a run's cost or tooling, by its
// reference, amount and who it went to (a PO with the charges on its invoice, the vendor's rounding, a PO priced by the
// payment). Nothing is linked until someone with "Can pay" confirms, or picks what it paid with Choose… (the CLI can
// link the exact ones by itself). Wise's transfer confirmation PDFs dropped here go to their transfer by the number in
// the name. The transfers are the app's usual table (DataTable: views, columns, resizing, Copy / CSV), filling the tab,
// with its filters in its header.

import { CheckCircle2, CloudDownload, EyeOff, FileUp, ListChecks, Undo2 } from "lucide-react";
import { type DragEvent, useMemo, useState } from "react";
import { noMatchHint, type Suggestion, transferLike, WISE_REASON_LABELS, WISE_REASONS, type WiseReason } from "../../../../shared/bills.ts";
import { addDays, planToday } from "../../../../shared/day.ts";
import type { WiseTransfer } from "../../../../shared/db.gen.ts";
import { amount, dayOf, postJson, useBillIndex, useCanPay, wiseFeature } from "../../lib/bills.ts";
import { shortDate } from "../../lib/costs.ts";
import { mutateWithResult } from "../../lib/mutate.ts";
import { setSearch, useSearch } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { uploadAttachment } from "../../ui/Attachments.tsx";
import { Badge, Button, cx, Input, Select } from "../../ui/controls.tsx";
import { CellControls, type Column, DataTable } from "../../ui/DataTable.tsx";
import { Popover } from "../../ui/Popover.tsx";
import { toast } from "../../ui/toast.tsx";
import { Why } from "../../ui/Why.tsx";
import { ChooseDialog } from "./ChooseDialog.tsx";

const FILTERS = [
  // Accounted for --- every sent transfer not linked or set aside with a reason, biggest first (migration 074).
  { key: "todo", label: "To account for" },
  { key: "suggested", label: "To confirm" },
  { key: "no_match", label: "No match" },
  { key: "linked", label: "Linked" },
  { key: "ignored", label: "Not bills (with why)" },
  { key: "new", label: "Not sent yet" },
  { key: "all", label: "All" },
] as const;
const CONFIDENCE = {
  exact: { tone: "ok", label: "Exact" },
  strong: { tone: "accent", label: "Likely" },
  possible: { tone: "neutral", label: "Maybe" },
} as const;

type Report = {
  read: number;
  added: number;
  updated: number;
  suggested: number;
  noMatch: number;
  payments: number;
  notSent: number;
  skipped: Record<string, number>;
};

/** A transfer's row: its suggestions, the one picked, and the hint for one nothing matches. */
type Row = WiseTransfer & { sugs: Suggestion[]; s: Suggestion | undefined; hint: string | null; receipt: boolean; paymentNumber: string };

export function WiseHistory() {
  const params = useSearch();
  const filter = params.get("show") ?? "todo";
  const transfers = useRows("wise_transfer");
  const payments = useRows("payment");
  const attachments = useRows("attachment");
  const ix = useBillIndex();
  const canPay = useCanPay();
  const wise = wiseFeature();
  const [since, setSince] = useState(addDays(planToday(), -180));
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<Report | null>(null);
  const [over, setOver] = useState(false);
  // Each transfer's picked suggestion (when it has several), the ones being confirmed, and the one Choose… is open for
  const [picks, setPicks] = useState<ReadonlyMap<string, number>>(() => new Map());
  const [confirming, setConfirming] = useState<ReadonlySet<string>>(() => new Set());
  const [choosingId, setChoosingId] = useState<string | null>(null);
  const choosing = choosingId ? transfers.find((t) => t.id === choosingId) : undefined;
  // Table views --- the View and Columns menus, in the table's header with its filters
  const counts = useMemo(() => {
    const c: Record<string, number> = { all: transfers.length, todo: 0 };
    for (const t of transfers) {
      c[t.match_status] = (c[t.match_status] ?? 0) + 1;
      if (toAccountFor(t)) c.todo++;
    }
    return c;
  }, [transfers]);
  const rows = useMemo<Row[]>(() => {
    const withReceipt = new Set(attachments.filter((a) => a.owner_type === "wise_transfer").map((a) => a.owner_id));
    return transfers
      .filter((t) => filter === "all" || (filter === "todo" ? toAccountFor(t) : t.match_status === filter))
      .map((t) => {
        const sugs = (Array.isArray(t.suggestions) ? t.suggestions : []) as Suggestion[];
        return {
          ...t,
          sugs,
          s: sugs[picks.get(t.id) ?? 0],
          // A payee that's a vendor with nothing open here: most likely an order from before HHL Ops.
          hint: t.match_status === "no_match" ? noMatchHint(ix, transferLike(t), dayOf) : null,
          receipt: withReceipt.has(t.id),
          paymentNumber: payments.find((p) => p.id === t.payment_id)?.number ?? "",
        };
      });
  }, [transfers, filter, picks, ix, attachments, payments]);
  const run = async (fn: () => Promise<Report>) => {
    setBusy(true);
    try {
      const r = await fn();
      setReport(r);
      toast.ok(`${r.read} transfers read: ${r.added} new, ${r.suggested} with a suggestion`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const statement = (file: File) =>
    run(async () => {
      const res = await fetch("/api/wise/statement", {
        method: "POST",
        headers: { "content-type": "text/csv", "x-requested-with": "hhl-ops" },
        body: await file.text(),
      });
      const body = (await res.json().catch(() => ({}))) as Report & { error?: string };
      if (!res.ok) throw new Error(body.error ?? "Couldn't read the statement");
      return body;
    });
  // Wise's confirmation PDFs: wise_transfer_confirmation__transfer__<id>__….pdf
  const receipts = async (files: File[]) => {
    for (const f of files) {
      const id = Number(f.name.match(/transfer__(\d+)__/)?.[1] ?? f.name.match(/(\d{9,12})/)?.[1]);
      const t = transfers.find((x) => Number(x.wise_transfer_id) === id);
      const p = payments.find((x) => Number(x.wise_transfer_id) === id);
      if (!t && !p) {
        toast.error(`${f.name}: no transfer ${id || "number in its name"} here yet (import the history or statement first)`);
        continue;
      }
      await uploadAttachment(p ? { type: "payment", id: p.id } : { type: "wise_transfer", id: t?.id as string }, f, { kind: "receipt" })
        .then(() => toast.ok(`${f.name} → transfer ${id}`))
        .catch((e) => toast.error((e as Error).message));
    }
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    const files = [...e.dataTransfer.files];
    const csv = files.find((f) => /\.csv$/i.test(f.name));
    if (csv) statement(csv);
    const pdfs = files.filter((f) => !/\.csv$/i.test(f.name));
    if (pdfs.length) receipts(pdfs);
  };
  const confirm = async (t: Row) => {
    const s = t.s;
    if (!s || s.blocked) return;
    setConfirming((c) => new Set(c).add(t.id));
    const paymentId = crypto.randomUUID();
    // The vendor's rounding (if any) goes with it, so a rounded suggestion is one click too.
    const { ok, result } = await mutateWithResult("wise_transfer.link", {
      id: t.id,
      payment_id: paymentId,
      targets: s.targets.map((x) => ({ kind: x.kind, id: x.id, ...(x.part ? { part: x.part } : {}), amount: x.amount })),
      adjustments: s.adjustments ?? [],
    });
    // With a token, Wise's own receipt is fetched for it.
    if (ok && t.source === "api") await postJson(`/api/wise/payments/${paymentId}/receipt`).catch(() => {});
    // Its fee goes on the run, unless the run has it already (entered by hand): then the toast says so.
    const r = (result ?? {}) as { fee?: string; keeps?: string[]; priced?: string[]; bills?: string[] };
    const fee = String(r.fee ?? "");
    const notes = [...(r.priced ?? []), ...(r.keeps ?? []), ...(fee && fee !== "added to the run" ? [fee] : [])];
    if (ok) toast.ok(`Transfer ${t.wise_transfer_id} linked to ${(r.bills ?? []).join(", ") || s.label}${notes.length ? `. ${notes.join("; ")}` : ""}`);
    setConfirming((c) => {
      const next = new Set(c);
      next.delete(t.id);
      return next;
    });
  };

  const columns: Column<Row>[] = [
    {
      key: "date",
      header: "Date",
      width: "96px",
      // Newest first, then the later transfer
      value: (t) => `${String(t.transfer_date ?? "").slice(0, 10)} ${String(t.wise_transfer_id).padStart(14, "0")}`,
      text: (t) => String(t.transfer_date ?? "").slice(0, 10),
      cell: (t) => <span className="text-[12px] text-muted">{shortDate(t.transfer_date)}</span>,
    },
    {
      key: "payee",
      header: "Payee",
      width: "minmax(130px,1fr)",
      value: (t) => t.payee_name || "Unknown payee",
      cell: (t) => (
        <span className="truncate font-medium" title={t.payee_name}>
          {t.payee_name || "Unknown payee"}
        </span>
      ),
    },
    {
      key: "reference",
      header: "Reference",
      width: "minmax(130px,1fr)",
      cell: (t) => (
        <span className="truncate font-mono text-[12px] text-muted" title={t.reference}>
          {t.reference || "(no reference)"}
        </span>
      ),
    },
    {
      key: "wise_transfer_id",
      header: "Transfer",
      width: "110px",
      optional: true,
      value: (t) => Number(t.wise_transfer_id),
      cell: (t) => <span className="font-mono text-[12px] text-muted">{t.wise_transfer_id}</span>,
    },
    {
      key: "receipt",
      header: "Receipt",
      width: "80px",
      optional: true,
      value: (t) => (t.receipt ? "receipt" : ""),
      cell: (t) => (t.receipt ? <span className="text-[12px] text-muted">receipt</span> : null),
    },
    {
      key: "note",
      header: "Note",
      width: "minmax(120px,1fr)",
      optional: true,
      cell: (t) => (
        <span className="truncate text-[12px] text-faint" title={t.note}>
          {t.note}
        </span>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      width: "112px",
      align: "right",
      value: (t) => Number(t.amount),
      text: (t) => amount(Number(t.amount), t.currency),
      cell: (t) => <span className="font-mono">{amount(Number(t.amount), t.currency)}</span>,
    },
    {
      key: "fee",
      header: "Fee",
      width: "96px",
      align: "right",
      value: (t) => Number(t.fee) || 0,
      text: (t) => (Number(t.fee) > 0 ? amount(Number(t.fee), t.currency) : ""),
      cell: (t) => (Number(t.fee) > 0 ? <span className="text-[12px] text-muted">+ {amount(Number(t.fee), t.currency)} fee</span> : null),
    },
    {
      key: "paid",
      header: "What it paid",
      width: "minmax(200px,1.4fr)",
      value: (t) => paidText(t),
      cell: (t) => (
        <CellControls className="gap-1.5 text-[12px]">
          {t.match_status === "linked" ? (
            <LinkedTo paymentId={t.payment_id} number={t.paymentNumber} />
          ) : t.match_status === "new" ? (
            <span className="truncate text-muted">Wise: {t.state.replaceAll("_", " ")} (matched once it's sent)</span>
          ) : t.s ? (
            <>
              <Badge tone={CONFIDENCE[t.s.confidence].tone}>{CONFIDENCE[t.s.confidence].label}</Badge>
              {t.sugs.length > 1 ? (
                <Select
                  value={String(picks.get(t.id) ?? 0)}
                  options={t.sugs.map((x, i) => ({ value: String(i), label: `${x.label} (${CONFIDENCE[x.confidence].label.toLowerCase()})` }))}
                  onChange={(e) => setPicks((m) => new Map(m).set(t.id, Number(e.target.value)))}
                  className="h-7 min-w-0 flex-1 text-[12px]"
                  aria-label="What it paid"
                />
              ) : (
                <Why title={t.s.label} lines={t.s.why.map((w) => ({ label: w }))} className="min-w-0 truncate">
                  <span className="truncate">{t.s.label}</span>
                </Why>
              )}
            </>
          ) : (
            <span className="truncate text-faint" title={t.reason_note || undefined}>
              {t.match_status === "ignored"
                ? `${t.reason ? WISE_REASON_LABELS[t.reason as WiseReason] : "Not a vendor payment"}${t.reason_note ? `: ${t.reason_note}` : ""}`
                : "Nothing matches"}
            </span>
          )}
        </CellControls>
      ),
    },
    {
      key: "why",
      header: "Why",
      width: "minmax(160px,1.2fr)",
      value: (t) => whyText(t),
      cell: (t) => {
        const why = t.s && t.match_status === "suggested" ? t.s.why.join(" · ") : "";
        const blocked = t.s?.blocked && t.match_status === "suggested" ? t.s.blocked : "";
        if (!why && !blocked && !t.hint) return null;
        return (
          <span
            className="truncate text-[12px]"
            title={[blocked, ...(t.s && t.match_status === "suggested" ? t.s.why : []), t.hint ?? ""].filter(Boolean).join("\n")}
          >
            {blocked && <span className="text-warn">{blocked}</span>}
            {blocked && why && <span className="text-muted"> · </span>}
            {why && <span className="text-muted">{why}</span>}
            {t.hint && <span className="text-muted">{t.hint}</span>}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "",
      width: "270px",
      value: () => null,
      cell: (t) => (
        <CellControls className="ml-auto justify-end gap-1.5">
          {t.match_status === "suggested" && canPay && t.s && (
            <Button size="sm" variant="primary" disabled={confirming.has(t.id) || !!t.s.blocked} title={t.s.blocked ?? ""} onClick={() => confirm(t)}>
              Confirm
            </Button>
          )}
          {(t.match_status === "suggested" || t.match_status === "no_match") && canPay && (
            <Button size="sm" variant="ghost" icon={<ListChecks className="size-3.5" />} onClick={() => setChoosingId(t.id)} title="Pick what it paid by hand">
              Choose…
            </Button>
          )}
          {(t.match_status === "suggested" || t.match_status === "no_match") && <NotABill id={t.id} />}
          {t.match_status === "ignored" && (
            <Button
              size="sm"
              variant="ghost"
              icon={<Undo2 className="size-3.5" />}
              onClick={() => mutateWithResult("wise_transfer.ignore", { id: t.id, ignored: false })}
            >
              Undo
            </Button>
          )}
        </CellControls>
      ),
    },
  ];

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 px-4 pt-4 pb-3 sm:px-6">
        <div
          className={cx("grid gap-3 rounded-lg border border-line bg-surface p-4 md:grid-cols-2", over && "outline-2 outline-accent outline-dashed")}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={onDrop}
        >
          <div>
            <div className="font-medium">From Wise</div>
            <div className="mt-0.5 text-[12px] text-muted">
              Reads past transfers from the business profile (a read-only token is enough) and suggests what each paid. Nothing is marked paid until you
              confirm.
            </div>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[12px] text-muted">Since</span>
              <Input type="date" value={since} max={planToday()} onChange={(e) => setSince(e.target.value)} className="h-8 w-40" />
              <Button
                icon={<CloudDownload className="size-4" />}
                disabled={busy || !wise.enabled}
                title={wise.enabled ? "" : "Wise isn't connected: Settings → Wise (or use a statement)"}
                onClick={() => run(() => postJson<Report>("/api/wise/history", { since }))}
              >
                Import from Wise
              </Button>
            </div>
          </div>
          <div>
            <div className="font-medium">From a statement</div>
            <div className="mt-0.5 text-[12px] text-muted">
              No token yet? Wise → Statements → USD → CSV. Drop it here (its account numbers are never read), with Wise's transfer confirmation PDFs: each goes
              to its transfer.
            </div>
            <label className="mt-2 inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md border border-line bg-surface px-3 font-medium hover:bg-hover">
              <FileUp className="size-4" /> Choose a CSV or PDFs
              <input
                type="file"
                accept=".csv,text/csv,application/pdf,image/*"
                multiple
                hidden
                onChange={(e) => {
                  const files = [...(e.target.files ?? [])];
                  const csv = files.find((f) => /\.csv$/i.test(f.name));
                  if (csv) statement(csv);
                  const rest = files.filter((f) => f !== csv);
                  if (rest.length) receipts(rest);
                  e.target.value = "";
                }}
              />
            </label>
          </div>
          {report && (
            <div className="col-span-full rounded-md bg-surface-2/60 px-3 py-2 text-[12px] text-muted">
              Read {report.read}: {report.added} new, {report.updated} updated, {report.payments} already payments here, {report.notSent} not sent yet ·{" "}
              {report.suggested} with a suggestion, {report.noMatch} with none
              {Object.entries(report.skipped).map(([k, n]) => ` · skipped ${n} ${k}`)}
            </div>
          )}
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-1 border-y border-line bg-surface px-4 py-2 sm:px-6">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setSearch({ show: f.key === "todo" ? null : f.key })}
            className={cx(
              "flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-[12px]",
              filter === f.key ? "border-accent bg-accent-soft text-accent-text" : "border-line bg-surface text-muted hover:bg-hover",
            )}
          >
            {f.label} <span className="font-mono">{counts[f.key] ?? 0}</span>
          </button>
        ))}
        {!canPay && <span className="ml-2 text-[12px] text-muted">Confirming a match marks bills paid: only people allowed to make payments can.</span>}
      </div>
      <div className="min-h-0 flex-1 bg-surface">
        <DataTable
          rows={rows}
          columns={columns}
          // To account for: the biggest first; the rest newest first
          defaultSort={filter === "todo" ? { key: "amount", desc: true } : { key: "date", desc: true }}
          exportName="Wise transfers"
          rowNoun={["transfer", "transfers"]}
          empty={
            <div className="p-6 text-center text-muted">
              {transfers.length ? "Nothing here." : "No Wise history yet. Import from Wise, or drop a statement CSV above."}
            </div>
          }
        />
      </div>
      {choosing && <ChooseDialog t={choosing} onClose={() => setChoosingId(null)} />}
    </div>
  );
}

/** What a transfer's "What it paid" says, for search, sorting and Copy / CSV. */
function paidText(t: Row) {
  if (t.match_status === "linked") return `${t.paymentNumber || "Linked"} paid`;
  if (t.match_status === "new") return `Wise: ${t.state.replaceAll("_", " ")} (matched once it's sent)`;
  if (t.s) return `${CONFIDENCE[t.s.confidence].label}: ${t.s.label}`;
  if (t.match_status === "ignored")
    return `${t.reason ? WISE_REASON_LABELS[t.reason as WiseReason] : "Not a vendor payment"}${t.reason_note ? `: ${t.reason_note}` : ""}`;
  return "Nothing matches";
}

/** Why it's suggested (or what stops it), and the hint for one nothing matches. */
function whyText(t: Row) {
  const sug = t.s && t.match_status === "suggested" ? [t.s.blocked ?? "", t.s.why.join(" · ")] : [];
  return [...sug, t.hint ?? ""].filter(Boolean).join(" · ");
}

/** Accounted for --- sent, and neither linked to what it paid nor set aside with a reason. */
const toAccountFor = (t: { state: string; match_status: string; payment_id: string | null }) =>
  t.state === "outgoing_payment_sent" && !t.payment_id && (t.match_status === "suggested" || t.match_status === "no_match");

/** Accounted for --- "Not a bill" asks why (migration 074): before HHL Ops, our own accounts, a refund… */
function NotABill({ id }: { id: string }) {
  const [reason, setReason] = useState<WiseReason | "">("");
  const [note, setNote] = useState("");
  return (
    <Popover
      width={300}
      button={(toggle) => (
        <Button size="sm" variant="ghost" icon={<EyeOff className="size-3.5" />} onClick={toggle} title="Not a vendor bill: say why">
          Not a bill
        </Button>
      )}
    >
      {(close) => (
        <div className="flex flex-col gap-1.5 text-[12px]">
          <div className="font-medium">Why isn't it a vendor bill?</div>
          {WISE_REASONS.map((r) => (
            <label key={r} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 hover:bg-hover">
              <input type="radio" name={`reason-${id}`} checked={reason === r} onChange={() => setReason(r)} />
              {WISE_REASON_LABELS[r]}
            </label>
          ))}
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={reason === "other" ? "What it was (needed)" : "A note (optional)"} />
          <Button
            size="sm"
            variant="primary"
            disabled={!reason || (reason === "other" && !note.trim())}
            onClick={async () => {
              const { ok } = await mutateWithResult("wise_transfer.ignore", { id, ignored: true, reason: reason as WiseReason, note: note.trim() });
              if (ok) close();
            }}
          >
            Set it aside
          </Button>
        </div>
      )}
    </Popover>
  );
}

/** What a linked transfer paid: its payment and bills (each opens). */
function LinkedTo({ paymentId, number }: { paymentId: string | null; number: string }) {
  const allocs = useRows("payment_allocation").filter((a) => a.payment_id === paymentId);
  const bills = useRows("bill");
  return (
    <span className="flex min-w-0 items-center gap-1.5 text-ok">
      <CheckCircle2 className="size-3.5 shrink-0" /> <span className="font-mono">{number || "Linked"}</span>
      <span className="text-muted">paid</span>
      {allocs.map((a) => {
        const b = bills.find((x) => x.id === a.bill_id);
        return (
          <button key={a.id} type="button" onClick={() => setSearch({ bill: a.bill_id })} className="truncate font-mono text-accent-text hover:underline">
            {b?.number ?? "a bill"}
          </button>
        );
      })}
    </span>
  );
}
